import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { IonSearchbar, ModalController } from '@ionic/angular/standalone';
import { LoadingComponent } from '@scandium-oy/ngx-scandium';
import { endOfToday, isBefore } from 'date-fns';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { SnapLinkComponent } from 'src/app/components/snap-links/item/snap-link.component';
import { SnapLink } from 'src/app/components/snap-links/snap-links.component';
import { SiteArea } from 'src/app/models/site-area.model';
import { Site } from 'src/app/models/site.model';
import { RoomTicket, TicketStatus } from 'src/app/models/ticket.model';
import { AreasService } from 'src/app/services/areas.service';
import { PlusService } from 'src/app/services/plus.service';
import { UsersService } from 'src/app/services/users.service';
import { WorkerTicketsService } from 'src/app/services/worker-tickets.service';
import { ShowFloorDialogComponent } from 'src/app/sites/edit-site/floors/show-floor.dialog';
import { adminActionRoles } from 'src/app/utility/role';
import { defaultRoom } from 'src/app/utility/room';
import { convertTimestamp } from 'src/app/utility/time';

@Component({
  selector: 'app-list-rooms',
  templateUrl: './list-rooms.component.html',
  styleUrls: ['./list-rooms.component.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    LoadingComponent,
    SnapLinkComponent,
  ],
})
export class ListRoomsComponent {
  private areasService = inject(AreasService);
  private modalCtrl = inject(ModalController);
  private plusService = inject(PlusService);
  private usersService = inject(UsersService);
  private workerTicketsService = inject(WorkerTicketsService);

  site = input.required<Site>();
  preSelected = input<SiteArea[]>();
  selected = output<SiteArea>();

  private filter$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private areas$ = combineLatest([toObservable(this.site), this.usersService.getCurrentUser()]).pipe(
    filter(([site, user]) => site != null && user != null),
    tap(() => this.loading.set(true)),
    switchMap(([site]) => this.areasService.getMyAreas(site.guid)),
    map((areas) => areas.filter((area) => area.name !== defaultRoom)),
    map((areas) => areas.map((area) => {
      const parentTickets$ = this.tickets$.pipe(
        map((tickets) => tickets
          .filter((it) => it.area.guid === area.guid)
          .filter((it) => [TicketStatus.created, TicketStatus.inProgress, TicketStatus.blocked, TicketStatus.paused].includes(it.status))),
        shareReplay(1),
      );
      const areaSnapLink = this.getThisSnapLink(area, parentTickets$);
      return Object.assign({}, area, { areaSnapLink });
    })),
    tap(() => this.loading.set(false)),
    shareReplay(1),
  );

  private tickets$ = combineLatest([toObservable(this.site), this.usersService.getCurrentUser()]).pipe(
    filter(([site, user]) => site != null && user != null),
    switchMap(([_site, _user]) => this.workerTicketsService.getMyTickets()),
    shareReplay(1),
  );

  roleS = computed(() => this.usersService.currentUserS()?.role);
  isCommercial = computed(() => this.site()?.commercial);
  loading = signal(false);

  readonly adminActionRoles = adminActionRoles;

  floors = signal<{
    name: string;
    commercial: (SiteArea & {
      areaSnapLink: SnapLink;
    })[];
    common: (SiteArea & {
      areaSnapLink: SnapLink;
    })[];
    url: string;
    show: { floor: boolean; commercial: boolean; common: boolean };
  }[]>([]);

  residentAreas$ = combineLatest([this.filter$, this.areas$]).pipe(
    map(([filterValue, items]) => items.filter((it) => it.type === 'residence')
      .filter((it) => it.name.toLowerCase().includes(filterValue.toLowerCase()) || it.areaSnapLink.name?.toLowerCase().includes(filterValue.toLowerCase()))),
    shareReplay(1),
  );

  commercialAreas$ = combineLatest([this.filter$, this.areas$]).pipe(
    map(([filterValue, items]) => items.filter((it) => it.type === 'commercial')
      .filter((it) => it.name.toLowerCase().includes(filterValue.toLowerCase()) || it.areaSnapLink.name?.toLowerCase().includes(filterValue.toLowerCase()))),
    shareReplay(1),
  );

  commonAreas$ = combineLatest([this.filter$, this.areas$]).pipe(
    map(([filterValue, items]) => items.filter((it) => it.type === 'common')
      .filter((it) => it.name.toLowerCase().includes(filterValue.toLowerCase()) || it.areaSnapLink.name?.toLowerCase().includes(filterValue.toLowerCase()))),
    shareReplay(1),
  );

  floors$ = combineLatest([this.filter$, this.areas$]).pipe(
    map(([filterValue, items]) => items
      .filter((it) => it.name.toLowerCase().includes(filterValue.toLowerCase()) || it.areaSnapLink.name?.toLowerCase().includes(filterValue.toLowerCase()))),
    map((areas) => {
      const siteFloors = this.site().floors;
      if (siteFloors?.length > 0) {
        const floors = Array.from<{
          name: string;
          commercial: (SiteArea & {
            areaSnapLink: SnapLink;
          })[];
          common: (SiteArea & {
            areaSnapLink: SnapLink;
          })[];
          url: string;
          show: { floor: boolean; commercial: boolean; common: boolean };
        }>(siteFloors.map((it) => ({ name: it.name, commercial: [], common: [], url: it.url, show: { floor: true, commercial: true, common: true } })));
        areas.map((it) => {
          it.selected = this.preSelected()?.some((itt) => itt.guid === it.guid) ?? false;
          const existing = floors.find((itt) => itt.name === it.floor);
          if (existing) {
            existing[it.type].push(it);
          }
        });
        this.floors.set(floors);
      }
    }),
    shareReplay(1),
  );

  private getThisSnapLink(area: SiteArea, tickets$: Observable<RoomTicket[]>) {
    const ticketCount$ = tickets$.pipe(
      map((items) => items?.length ?? 0),
      shareReplay(1),
    );

    const names = area.users?.filter((it) => it.end == null || isBefore(endOfToday(), convertTimestamp(it.end))).map((it) => {
      const splitted = it.name.split(' ');
      return splitted[splitted.length - 1];
    });
    const setOfNames = new Set(names);
    const name = Array.from(setOfNames).join(' ') ?? '';

    const snapLink: SnapLink = {
      info: area.name,
      name,
      icon: 'documents',
      notification$: ticketCount$,
      action: () => this.selected.emit(area),
    };
    return snapLink;
  }

  onFilter(searchbar: IonSearchbar) {
    const filterValue = searchbar.value;
    this.filter$.next(filterValue);
  }

  onBlueprint(item: { name: string; url: string }) {
    this.modalCtrl.create({ component: ShowFloorDialogComponent, componentProps: { item, site: this.site(), disabled: true }, cssClass: ['modal-fullscreen'] }).then((m) => {
      m.present();
    });
  }

  addRooms(type: 'common' | 'residence' | 'commercial', floor?: string) {
    this.plusService.onAddAreas(this.site().guid, type, floor);
  }
}
